import {
  Button,
  Checkbox,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from "@material-ui/core";
import AssignmentOutlinedIcon from "@material-ui/icons/AssignmentOutlined";
import CheckCircleOutlineOutlinedIcon from "@material-ui/icons/CheckCircleOutlineOutlined";
import ListAltOutlinedIcon from "@material-ui/icons/ListAltOutlined";
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from "react";
import { FaRegTrashAlt } from "react-icons/fa";
import { useSelector } from "react-redux";
import { selectAvailableRestrictions } from "../../../../redux/features/lender-request/removeDeliveryRestrictionSlice";
import { getRestrictionComparer } from "../../../shared/services/DeliveryRestrictionService";

import "./DeliveryRestrictionsReviewSection.css";

/**
 * Returns true when tracker flags explicitly mark a restriction inactive.
 * Returns false when flags explicitly mark the restriction active.
 * Returns undefined when flags are missing and caller should infer state from context.
 */
const getTrackerInactiveState = (tracker) => {
  if (tracker?.inactive === true || tracker?.active === false) return true;
  if (tracker?.inactive === false || tracker?.active === true) return false;
  return undefined;
};

const normalizeTrackerStatus = (status) => String(status || "").trim().toLowerCase();

/**
 * Maps a raw tracker status to the reviewer-facing decision label.
 */
const mapDecisionLabel = (status, options = {}) => {
  const { inactive, isInCurrent } = options;
  const normalizedStatus = normalizeTrackerStatus(status);

  if (normalizedStatus === "accept") return "Removed";
  if (normalizedStatus === "reject") return "Retained";
  if (normalizedStatus === "modify") {
    if (inactive === true) return "Removed";
    if (inactive === false) return "Added";

    // Fallback for payloads that omit active/inactive flags:
    // infer from current active restrictions (activeDeliveryRestrictionIds).
    return isInCurrent ? "Removed" : "Added";
  }
  return "Unknown";
};

/**
 * Final Delivery Restrictions behavior:
 * - Accept => Removed
 * - Reject => Retained
 * - Modify => Removed when restriction is in current active restrictions, else Added
 */
const mapFinalDecisionLabel = (status, isInCurrent) => {
  const normalizedStatus = normalizeTrackerStatus(status);

  if (normalizedStatus === "accept") return "Removed";
  if (normalizedStatus === "reject") return "Retained";
  if (normalizedStatus === "modify") return isInCurrent ? "Removed" : "Added";
  return "Unknown";
};

const renderSectionHeader = (title, IconComponent, toneClass) => (
  <h3 className="request-details-subheader review-restrictions-header" style={{ marginBottom: 10 }}>
    <span className={`review-restrictions-header-icon ${toneClass}`} aria-hidden="true">
      <IconComponent fontSize="small" />
    </span>
    <span>{title}</span>
  </h3>
);

/**
 * DeliveryRestrictionsReviewSection
 *
 * Renders request-time/current delivery restrictions and supports reviewer decisions.
 * In read-only Action Taken view, shows Accept/Reject/Modify from fetch-status-change-requests.
 */
const DeliveryRestrictionsReviewSection = ({
  requestedRestrictionIDs,
  currentRestrictionIDs,
  currentRestrictionIDsFromRequest,
  removeDrTrackers,
  showDecisionStatus,
  showCombinedCurrentAndRequested,
  showRequestedOnly,
  onSelectRestrictions,
  canReviewRestrictions,
  historicMode,
  showDecisionColumn = showDecisionStatus,
  showFinalRestrictions = true,
  isRequestDeclined = false
}) => {
  const availableRestrictions = useSelector(selectAvailableRestrictions);

  const availableRestrictionsMap = useMemo(
    () => new Map((availableRestrictions || [])
      .filter(restriction => restriction?.restrictionId != null)
      .map(restriction => [Number(restriction.restrictionId), restriction])),
    [availableRestrictions]
  );

  const trackerByRestrictionId = useMemo(
    () => new Map((removeDrTrackers || [])
      .filter(tracker => tracker?.restrcnId != null)
      .map(tracker => [Number(tracker.restrcnId), tracker])),
    [removeDrTrackers]
  );

  const restrictionById = useMemo(() => {
    const restrictions = new Map(availableRestrictionsMap);

    trackerByRestrictionId.forEach((tracker, restrictionId) => {
      if (!restrictions.has(restrictionId)) {
        restrictions.set(restrictionId, {
          restrictionId,
          restrictionName: tracker.restrcnName,
          restrictionShortName: tracker.restrcnShortName,
        });
      }
    });

    return restrictions;
  }, [availableRestrictionsMap, trackerByRestrictionId]);

  const requestedIdSet = useMemo(
    () => new Set((requestedRestrictionIDs || [])
      .map(Number)
      .filter(Number.isFinite)),
    [requestedRestrictionIDs]
  );

  const currentIdSet = useMemo(
    () => new Set((currentRestrictionIDs || [])
      .map(Number)
      .filter(Number.isFinite)),
    [currentRestrictionIDs]
  );

  const requestTimeRestrictions = useMemo(() => {
    const ids = (historicMode && currentRestrictionIDsFromRequest)
      ? currentRestrictionIDsFromRequest
      : currentRestrictionIDs;

    return (ids || [])
      .map(id => restrictionById.get(Number(id)))
      .filter(Boolean)
      .sort(getRestrictionComparer());
  }, [historicMode, currentRestrictionIDs, currentRestrictionIDsFromRequest, restrictionById]);

  const decisionViewRestrictions = useMemo(() => {
    if (!(showDecisionStatus || showCombinedCurrentAndRequested)) return [];

    const rowsById = new Map();

    // In Action Taken/Completed views, show both the project's current restrictions
    // and the originally requested ones in a single table.
    requestTimeRestrictions.forEach((restriction) => {
      rowsById.set(restriction.restrictionId, {
        restrictionId: restriction.restrictionId,
        restrictionName: restriction.restrictionName || "-",
        restrictionShortName: restriction.restrictionShortName || "-",
      });
    });

    requestedIdSet.forEach((restrictionId) => {
      const known = restrictionById.get(Number(restrictionId));
      if (!known) return;

      rowsById.set(restrictionId, {
        restrictionId,
        restrictionName: known?.restrictionName || "-",
        restrictionShortName: known?.restrictionShortName || "-",
      });
    });

    (removeDrTrackers || []).forEach((tracker) => {
      const restrictionId = Number(tracker?.restrcnId);
      if (!Number.isFinite(restrictionId)) return;

      const known = restrictionById.get(Number(restrictionId));
      rowsById.set(restrictionId, {
        restrictionId,
        restrictionName: known?.restrictionName || tracker?.restrcnName || "-",
        restrictionShortName: known?.restrictionShortName || tracker?.restrcnShortName || "-",
      });
    });

    return Array.from(rowsById.values()).sort(getRestrictionComparer());
  }, [showDecisionStatus, showCombinedCurrentAndRequested, removeDrTrackers, restrictionById, requestTimeRestrictions, requestedIdSet]);

  const requestedOnlyRestrictions = useMemo(() => {
    return (requestedRestrictionIDs || [])
      .map((id) => restrictionById.get(Number(id)))
      .filter(Boolean)
      .sort(getRestrictionComparer());
  }, [requestedRestrictionIDs, restrictionById]);

  const currentDeliveryRestrictions = useMemo(() => {
    return (currentRestrictionIDs || [])
      .map((id) => restrictionById.get(Number(id)))
      .filter(Boolean)
      .sort(getRestrictionComparer());
  }, [currentRestrictionIDs, restrictionById]);

  const displayedRestrictions = (showDecisionStatus || showCombinedCurrentAndRequested)
    ? decisionViewRestrictions
    : showRequestedOnly
      ? requestedOnlyRestrictions
      : requestTimeRestrictions;

  const [approvedIdSet, setApprovedIdSet] = useState(new Set());
  const [addedRestrictionRows, setAddedRestrictionRows] = useState([]);
  const [isAddingRestriction, setIsAddingRestriction] = useState(false);

  useEffect(() => {
    setApprovedIdSet(new Set(isRequestDeclined ? [] : Array.from(requestedIdSet)));
  }, [isRequestDeclined, requestedIdSet]);

  const remainingRestrictions = useMemo(() => {
    if (!availableRestrictions?.length) return [];

    const currentIds = new Set(requestTimeRestrictions.map(r => r.restrictionId));
    const addedIds = new Set(addedRestrictionRows.map(r => r.restrictionId));

    return availableRestrictions
      .filter(r => !currentIds.has(r.restrictionId) && !addedIds.has(r.restrictionId))
      .sort(getRestrictionComparer());
  }, [availableRestrictions, requestTimeRestrictions, addedRestrictionRows]);

  const calculatedFinalRestrictions = useMemo(() => {
    const keptRestrictions = requestTimeRestrictions.filter(
      (restriction) => !approvedIdSet.has(restriction.restrictionId)
    );

    return [...keptRestrictions, ...addedRestrictionRows].sort(getRestrictionComparer());
  }, [requestTimeRestrictions, approvedIdSet, addedRestrictionRows]);

  const finalRestrictions = useMemo(() => {
    if (!showDecisionStatus) return calculatedFinalRestrictions;
    const rowsById = new Map();

    // Baseline final-state rows: anything currently active that was not part of
    // the original remove request should remain in the Final DR list.
    currentDeliveryRestrictions
      .filter((restriction) => !requestedIdSet.has(restriction.restrictionId))
      .forEach((restriction) => {
        rowsById.set(restriction.restrictionId, {
          restrictionId: restriction.restrictionId,
          restrictionName: restriction.restrictionName || "-",
          restrictionShortName: restriction.restrictionShortName || "-",
        });
      });

    (removeDrTrackers || [])
      .forEach((tracker) => {
        const restrictionId = Number(tracker?.restrcnId);
        if (!Number.isFinite(restrictionId)) return;

        const label = mapFinalDecisionLabel(tracker?.status, currentIdSet.has(restrictionId));
        if (label === "Removed") {
          rowsById.delete(restrictionId);
          return;
        }
        if (label === "Unknown") return;

        const known = restrictionById.get(Number(restrictionId));
        rowsById.set(restrictionId, {
          restrictionId,
          restrictionName: known?.restrictionName || tracker?.restrcnName || "-",
          restrictionShortName: known?.restrictionShortName || tracker?.restrcnShortName || "-",
        });
      });

    return Array.from(rowsById.values()).sort(getRestrictionComparer());
  }, [showDecisionStatus, calculatedFinalRestrictions, removeDrTrackers, restrictionById, requestedIdSet, currentIdSet, currentDeliveryRestrictions]);

  // Keep the parent synced only for editable mode.
  useEffect(() => {
    if (!showDecisionStatus && onSelectRestrictions) {
      onSelectRestrictions(calculatedFinalRestrictions.map((restriction) => restriction.restrictionId));
    }
  }, [showDecisionStatus, onSelectRestrictions, calculatedFinalRestrictions]);

  const showAddRestrictions = canReviewRestrictions &&
    !showRequestedOnly &&
    (availableRestrictions?.length || 0) > (requestTimeRestrictions?.length || 0);

  const handleAddRestrictionRow = () => {
    setIsAddingRestriction(true);
  };

  const handleDecisionChange = (restrictionId, isApproved) => {
    setApprovedIdSet((prevApprovedIdSet) => {
      const newSet = new Set(prevApprovedIdSet);
      if (isApproved) newSet.add(restrictionId);
      else newSet.delete(restrictionId);
      return newSet;
    });
  };

  const handleSelectAddedRestriction = (restrictionId) => {
    const restriction = restrictionById.get(Number(restrictionId));
    if (!restriction) return;

    setAddedRestrictionRows((prev) => [...prev, restriction].sort(getRestrictionComparer()));
    setIsAddingRestriction(false);
  };

  const handleRemoveAddedRestrictionRow = (restrictionId) => {
    setAddedRestrictionRows((prev) => prev.filter((row) => row.restrictionId !== restrictionId));
  };

  return (
    <>
      <div className="review-restrictions-section">
        {renderSectionHeader("Current Delivery Restrictions", ListAltOutlinedIcon, "current-restrictions-tone")}
        {!currentDeliveryRestrictions.length ? (
          <p className="no-restrictions-message">No delivery restrictions on project.</p>
        ) : (
          <Table className="review-restrictions-table current-delivery-restrictions-table">
            <TableHead>
              <TableRow>
                <TableCell className="restriction-name-column">Restriction Name</TableCell>
                <TableCell>Short Name</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {currentDeliveryRestrictions.map((restriction) => (
                <TableRow key={restriction.restrictionId}>
                  <TableCell className="restriction-name-column">{restriction.restrictionName || "-"}</TableCell>
                  <TableCell>{restriction.restrictionShortName || "-"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <div className="review-restrictions-section">
        {renderSectionHeader("Requested Delivery Restrictions", AssignmentOutlinedIcon, "requested-restrictions-tone")}
        {displayedRestrictions.length ? (
          <Table className="review-restrictions-table current-restrictions-table" style={{ marginBottom: 0 }}>
            <TableHead>
              <TableRow>
                <TableCell className="restriction-name-column">Restriction Name</TableCell>
                <TableCell>Short Name</TableCell>
                <TableCell className="checkbox-column">Removal Requested</TableCell>
                {canReviewRestrictions && (
                  <TableCell className="checkbox-column">
                    {isRequestDeclined ? "Removal Rejected" : "Removal Approved"}
                  </TableCell>
                )}
                {showDecisionColumn && showDecisionStatus && <TableCell className="checkbox-column">Decision</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedRestrictions.map((currentRestriction) => {
                const isRequested = requestedIdSet.has(currentRestriction.restrictionId);
                const isApproved = approvedIdSet.has(currentRestriction.restrictionId);
                const tracker = trackerByRestrictionId.get(currentRestriction.restrictionId);
                const decisionStatus = tracker?.status;
                const decisionLabel = decisionStatus
                  ? mapDecisionLabel(decisionStatus, {
                    inactive: getTrackerInactiveState(tracker),
                    isRequested,
                    isInCurrent: currentIdSet.has(currentRestriction.restrictionId),
                  })
                  : "-";

                return (
                  <TableRow
                    key={currentRestriction.restrictionId}
                    className={isApproved ? "restriction-row-approved" : undefined}
                  >
                    <TableCell className="restriction-name-column">{currentRestriction.restrictionName || "-"}</TableCell>
                    <TableCell>{currentRestriction.restrictionShortName || "-"}</TableCell>
                    <TableCell className="checkbox-column">
                      {normalizeTrackerStatus(decisionStatus) !== "modify" && isRequested && (
                        <Checkbox
                          checked={isRequested}
                          disabled={true}
                          style={{ color: "rgba(0, 0, 0, 0.26)" }}
                        />
                      )}
                    </TableCell>
                    {canReviewRestrictions && (
                      <TableCell className="checkbox-column">
                        <Checkbox
                          checked={isApproved}
                          onChange={(e) => handleDecisionChange(currentRestriction.restrictionId, e.target.checked)}
                          style={{ color: "#085280" }}
                        />
                      </TableCell>
                    )}
                    {showDecisionColumn && showDecisionStatus && (
                      <TableCell className="checkbox-column">
                        {decisionLabel}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        ) : ((addedRestrictionRows.length === 0 && !isAddingRestriction) || !showAddRestrictions) && (
          <p className="no-restrictions-message">No delivery restrictions on project.</p>
        )}
        {showAddRestrictions && (
          <>
            {(addedRestrictionRows.length > 0 || isAddingRestriction) && (
              <Table className="review-restrictions-table added-restrictions-table" style={{ marginTop: 0 }}>
                <TableBody>
                  {addedRestrictionRows.map((restriction) => (
                    <TableRow key={restriction.restrictionId}>
                      <TableCell className="restriction-name-column">{restriction.restrictionName || "-"}</TableCell>
                      <TableCell>{restriction.restrictionShortName || "-"}</TableCell>
                      <TableCell className="cancel-add-restriction-column">
                        <IconButton
                          size="small"
                          aria-label="Remove added restriction"
                          onClick={() => handleRemoveAddedRestrictionRow(restriction.restrictionId)}
                        >
                          <FaRegTrashAlt style={{ fill: "red" }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {isAddingRestriction && (
                    <TableRow className="insert-restriction-row">
                      <TableCell style={{ width: 'auto' }} colSpan={2}>
                        <select
                          className="add-restriction-select"
                          value=""
                          aria-label="Select restriction to add"
                          onChange={(e) => handleSelectAddedRestriction(Number(e.target.value))}
                        >
                          <option value="" disabled>Select a restriction...</option>
                          {remainingRestrictions.map((restriction) => (
                            <option key={restriction.restrictionId} value={restriction.restrictionId}>{restriction.restrictionShortName || "-"}</option>
                          ))}
                        </select>
                      </TableCell>
                      <TableCell className="cancel-add-restriction-column">
                        <IconButton
                          size="small"
                          aria-label="Cancel adding restriction"
                          onClick={() => setIsAddingRestriction(false)}
                        >
                          <FaRegTrashAlt style={{ fill: "red" }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
            <Button
              className="add-restriction-button"
              onClick={handleAddRestrictionRow}
              disabled={remainingRestrictions.length === 0}
              style={{ display: isAddingRestriction ? 'none' : undefined }}
            >
              + Add Restriction
            </Button>
          </>
        )}
      </div>

      {showFinalRestrictions && (
        <div className="review-restrictions-section">
          {renderSectionHeader("Final Delivery Restrictions", CheckCircleOutlineOutlinedIcon, "final-restrictions-tone")}
          {!finalRestrictions.length ? (
            <p className="no-restrictions-message">No delivery restrictions on project.</p>
          ) : (
            <Table className="review-restrictions-table final-restrictions-table">
              <TableHead>
                <TableRow>
                  <TableCell className="restriction-name-column">Restriction Name</TableCell>
                  <TableCell>Short Name</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {finalRestrictions.map((restriction) => (
                  <TableRow key={restriction.restrictionId}>
                    <TableCell className="restriction-name-column">{restriction.restrictionName || "-"}</TableCell>
                    <TableCell>{restriction.restrictionShortName || "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      )}

    </>
  );
};

DeliveryRestrictionsReviewSection.propTypes = {
  requestedRestrictionIDs: PropTypes.arrayOf(PropTypes.number),
  currentRestrictionIDs: PropTypes.arrayOf(PropTypes.number),
  currentRestrictionIDsFromRequest: PropTypes.arrayOf(PropTypes.number),
  removeDrTrackers: PropTypes.arrayOf(PropTypes.shape({
    restrcnId: PropTypes.number,
    status: PropTypes.string,
    restrcnName: PropTypes.string,
    restrcnShortName: PropTypes.string,
  })),
  showDecisionStatus: PropTypes.bool,
  showCombinedCurrentAndRequested: PropTypes.bool,
  showRequestedOnly: PropTypes.bool,
  onSelectRestrictions: PropTypes.func,
  canReviewRestrictions: PropTypes.bool,
  historicMode: PropTypes.bool,
  showDecisionColumn: PropTypes.bool,
  showFinalRestrictions: PropTypes.bool,
  isRequestDeclined: PropTypes.bool,
};

export default DeliveryRestrictionsReviewSection;
